import React, { createContext, useMemo, useEffect } from 'react';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { Token } from '@marageti/z4-sdk/lib/auth';
import {
  hasAuthorizedRole,
  LocalStorageService,
  useApiClient,
} from '@marageti/z4-lib';
import { ProviderProps } from '../../types';
import { ValueProps } from './types';
import {
  setAuthenticatedTeamMember,
  setIsAuthenticated,
  resetAuthenticatedTeamMember,
} from './actions';
import useAuthReducer from './use-auth-reducer';
import { authorizedRoles, getMatchingUser } from '../../../utils/auth';

const getCookieValue = (name: string): any | undefined => {
  // First try to find environment-specific oryx cookie
  const env = process.env.REACT_APP_STAGE || 'prod';
  const oryxCookieMatch = document.cookie.match(new RegExp(`(^| )${name}_oryx_${env}=([^;]+)`));
  if (oryxCookieMatch) {
    try {
      return JSON.parse(decodeURIComponent(oryxCookieMatch[2]));
    } catch (error) {
      return decodeURIComponent(oryxCookieMatch[2]);
    }
  }

  // Fall back to default cookie
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  if (match) {
    try {
      return JSON.parse(decodeURIComponent(match[2]));
    } catch (error) {
      // If the cookie value is not a valid JSON string, return it as is
      return decodeURIComponent(match[2]);
    }
  }
  return undefined;
};

const LocalStorageClient = new LocalStorageService();

// On localhost the user_session cookie cannot be set (it is scoped to .zicasso.com), so local dev
// has to keep relying on the localStorage token.
const isLocalhost = typeof window !== 'undefined'
  && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

// setInternalTrafficCookie tags this team-member device as internal traffic client-side, as a
// backup to the server-side cookie set by travel-service on login.
const setInternalTrafficCookie = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const domain = window.location.hostname.endsWith('zicasso.com') ? '; domain=.zicasso.com' : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const oneYear = 365 * 24 * 60 * 60;
  document.cookie = `zicasso_internal=true; path=/; max-age=${oneYear}; SameSite=Lax${domain}${secure}`;
};

export const AuthStore = createContext({} as ValueProps);

export const AuthProvider = ({ children }: ProviderProps) => {
  const [authState, authDispatch] = useAuthReducer();
  const apiClient = useApiClient();

  const removeAuthenticatedTeamMember = () => apiClient.authClient.logout()
    .finally(() => {
      LocalStorageClient.removeFromLocalStorage('accessToken');
      LocalStorageClient.removeFromLocalStorage('email');
      authDispatch(setIsAuthenticated(false));
      authDispatch(resetAuthenticatedTeamMember());
    });

  const getAccessToken = (): string | undefined => {
    // Internal apps require the user_session cookie: if it is missing the user is logged out and
    // forced to re-login, which re-issues the internal-traffic cookie. The localStorage token is
    // only used as a complement once the cookie is confirmed present. getCookieValue already falls
    // back from the oryx-specific cookie to the generic one.
    const userSessionCookie = getCookieValue('user_session');
    const cookieSessionId = userSessionCookie?.personType === 'teamMember'
      ? userSessionCookie?.sessionId
      : undefined;

    const localStorageAccessToken = LocalStorageClient.getFromLocalStorage('accessToken');

    // On localhost the cookie can't be set, so keep the localStorage fallback for local dev only.
    if (!cookieSessionId) {
      return isLocalhost ? localStorageAccessToken ?? undefined : undefined;
    }
    return localStorageAccessToken || cookieSessionId;
  };

  const setAccessToken = (accessToken: string) => {
    // Set Bearer token. This is required for when accessing the GetToken method through a user_session cookie.
    LocalStorageClient.addToLocalStorage('accessToken', accessToken);
    apiClient.instance.setAuthToken(accessToken);
  };

  const authorizeUserFromToken = (accessToken: string) => {
    // Check token status and get user
    apiClient.authClient
      .getToken(accessToken)
      .then((data: Token) => apiClient.peopleClient.getTeamMember(data.email))
      .then((data: TeamMember) => {
        const matchingUser = getMatchingUser(data.email, data.userInfo);
        if (!matchingUser || !hasAuthorizedRole(matchingUser.roles, authorizedRoles)) {
          throw new Error('Not Authorized');
        }
        LocalStorageClient.addToLocalStorage('email', data.email);
        authDispatch(setAuthenticatedTeamMember(data as TeamMember));
        authDispatch(setIsAuthenticated(true));
      })
      .catch(() => {
        removeAuthenticatedTeamMember();
      });
  };

  useEffect(() => {
    const accessToken = getAccessToken();
    if (accessToken) {
      setInternalTrafficCookie();
      setAccessToken(accessToken);
      authorizeUserFromToken(accessToken);
    } else {
      removeAuthenticatedTeamMember();
    }
  }, []);

  const value: ValueProps = useMemo(
    () => ({
      ...authState,
      authDispatch,
      removeAuthenticatedTeamMember,
      setIsAuthenticated: (isAuthenticated: boolean) => authDispatch(setIsAuthenticated(isAuthenticated)),
      setAuthenticatedTeamMember: (teamMember: TeamMember) => authDispatch(setAuthenticatedTeamMember(teamMember)),
    }),
    [authState, authDispatch],
  );

  return <AuthStore.Provider value={value}>{children}</AuthStore.Provider>;
};
